/** @file Streaming PNG encoder: rows in, fixed-size IDAT chunks out through CompressionStream(deflate). */
import { invariant } from '@pose2d/std/invariant';
import { createPngStream } from './_internal/createPngStream';
import type { PngWriter } from './pngWriter';

/** Starts a PNG of width × height; raster writes one band at a time so a full-size buffer never exists. */
export function createPngWriter(width: number, height: number): PngWriter {
  invariant(Number.isInteger(width) && Number.isInteger(height) && width > 0 && height > 0, `PNG size ${width}x${height}`);
  const stride = width * 4;
  const stream = createPngStream({ width, height, colorType: 6, before: [] });
  const above = new Uint8Array(stride); // the row above the first one is zero (PNG Up filter)
  let rowsLeft = height;
  let finished = false;
  return {
    async writeRows(rows, rowCount) {
      invariant(!finished, 'PngWriter.writeRows after finish');
      invariant(Number.isInteger(rowCount) && rowCount >= 0 && rowCount <= rowsLeft, `writeRows: ${rowCount} rows, ${rowsLeft} left`);
      invariant(rows.length >= rowCount * stride, `writeRows: ${rows.length} bytes < ${rowCount} rows of ${stride}`);
      rowsLeft -= rowCount;
      await stream.write(filterUp(rows, rowCount, stride, above));
    },
    async finish() {
      invariant(!finished, 'PngWriter.finish called twice');
      invariant(rowsLeft === 0, `PngWriter.finish: ${rowsLeft} rows missing`);
      finished = true;
      return stream.finish();
    },
  };
}

/** Up-filtered scanlines (filter byte 2, then row − row above, mod 256); `above` is updated to the last row. */
function filterUp(rows: Uint8Array | Uint8ClampedArray, rowCount: number, stride: number, above: Uint8Array): Uint8Array {
  const out = new Uint8Array(rowCount * (stride + 1));
  for (let r = 0; r < rowCount; r++) {
    const src = r * stride;
    const dst = r * (stride + 1);
    const prev = r === 0 ? above : rows.subarray(src - stride, src);
    out[dst] = 2;
    for (let i = 0; i < stride; i++) out[dst + 1 + i] = ((rows[src + i] ?? 0) - (prev[i] ?? 0)) & 0xff;
  }
  if (rowCount > 0) above.set(rows.subarray((rowCount - 1) * stride, rowCount * stride));
  return out;
}
